import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClockIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
import { Account } from '../types';
import { useActivityFeed, getActivityIcon, getActivityColor, groupActivitiesByDate } from '../lib/activityFeed';
interface ActivityFeedProps {
  account: Account;
}
export function ActivityFeed({
  account
}: ActivityFeedProps) {
  const {
    getActivities
  } = useActivityFeed();
  const activities = getActivities(account.id);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Ensure scroll starts at top when component mounts
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, []);
  const groupedActivities = groupActivitiesByDate(activities);
  const hasActivities = activities.length > 0;
  if (!hasActivities) {
    return <div className="h-full flex flex-col" data-id="element-87">
        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-88">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center mb-6 shadow-xl ring-2 ring-slate-700/30" data-id="element-89">
            <SparklesIcon className="w-10 h-10 text-slate-500" data-id="element-90" />
          </div>
          <h3 className="text-base font-bold text-white mb-2" data-id="element-91">
            No Activity Yet
          </h3>
          <p className="text-sm text-slate-400 max-w-xs" data-id="element-92">
            Activity will appear here as you interact with {account.name}
          </p>
        </div>
      </div>;
  }
  return <div className="h-full flex flex-col" data-id="element-93">
      {/* Activity List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-94">
        {Object.entries(groupedActivities).map(([dateGroup, groupActivities]) => <div key={dateGroup} className="space-y-2" data-id="element-95">
              {/* Date Header */}
              <div className="flex items-center gap-2 px-1" data-id="element-96">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-97">
                  {dateGroup}
                </span>
                <div className="flex-1 h-px bg-slate-800/60" data-id="element-98" />
              </div>
              {/* Activities */}
              <AnimatePresence mode="popLayout" data-id="element-99">
                {groupActivities.map(activity => {
            const colorClass = getActivityColor(activity.type);
            const bgColorMap = {
              'text-blue-400': 'bg-blue-500/5 border-blue-500/20',
              'text-green-400': 'bg-green-500/5 border-green-500/20',
              'text-purple-400': 'bg-purple-500/5 border-purple-500/20',
              'text-amber-400': 'bg-amber-500/5 border-amber-500/20',
              'text-pink-400': 'bg-pink-500/5 border-pink-500/20',
              'text-slate-400': 'bg-slate-500/5 border-slate-500/20'
            };
            const bgClass = bgColorMap[colorClass as keyof typeof bgColorMap] || 'bg-slate-500/5 border-slate-500/20';
            return <motion.div key={activity.id} initial={{
              opacity: 0,
              x: -20,
              scale: 0.95
            }} animate={{
              opacity: 1,
              x: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              x: 20,
              scale: 0.95
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} className={`group relative bg-slate-900/40 backdrop-blur-sm border rounded-lg p-3 hover:bg-slate-800/40 transition-all duration-200 overflow-hidden ${bgClass.split(' ')[1]}`} data-id="element-100">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-101" />
                      <div className="relative flex items-start gap-2.5" data-id="element-102">
                        {/* Icon */}
                        <div className={`w-6 h-6 rounded-lg ${bgClass.split(' ')[0]} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`} data-id="element-103">
                          {getActivityIcon(activity.type)}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0" data-id="element-104">
                          <p className="text-xs text-slate-300 leading-relaxed group-hover:text-white transition-colors" data-id="element-105">
                            {activity.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5" data-id="element-106">
                            <span className="text-[10px] text-slate-500 font-medium" data-id="element-107">
                              {new Date(activity.timestamp).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                            </span>
                            <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-108" />
                            <span className={`text-[10px] font-semibold uppercase tracking-wider ${colorClass}`} data-id="element-109">
                              {activity.type}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>;
          })}
              </AnimatePresence>
            </div>)}
      </div>
      {/* Footer */}
      <div className="relative px-5 py-2.5 border-t border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 backdrop-blur-xl flex-shrink-0" data-id="element-110">
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-green-500/5 pointer-events-none" data-id="element-111" />
        <div className="relative text-center" data-id="element-112">
          <p className="text-[10px] text-slate-500 font-medium" data-id="element-113">
            Updates in real-time
          </p>
        </div>
      </div>
    </div>;
}